import React__default from 'react';
import { SVG, StopOne, StopTwo } from './styles.js';
import AnimatedLogo from './AnimatedLogo.js';

/* eslint-disable @typescript-eslint/no-unnecessary-condition */

// orange-yellow: #F96C2C - #F9D95D
// green-blue: #1FDA96 - #3FB3F2
// pink-orange: #E54074 - #F96C2C

/**
 *
 * Use a Logo component wherever you need to easily add Appfire branding. 
 * 
 * Three variants are available: 'solid', 'gradient', and 'animated'.
 *
 * If a 'gradient' is passed, the default gradient svg stops to use for Appfire branding are:
 * 
 * ```jsx
// orange-yellow gradient values 
// stopColor1: #F96C2C
// stopColor2: #F9D95D
//
// green-blue gradient values 
// stopColor1: #1FDA96 
// stopColor2: #3FB3F2
//
// pink-orange gradient values 
// stopColor1: #E54074 
// stopColor2: #F96C2C
//
 * ```
 * The value passed to the `color` prop will only be rendered with `solid` variant logo components.
 * 
 * Animated variants take a `color` prop and display an animated gradient effect on hover.
 * 
 */
const Logo = ({
  size = 24,
  variant = 'solid',
  stopColor1 = '#1FDA96',
  stopColor2 = '#3FB3F2',
  color,
  sx: sxProp
}) => {
  let Component;
  if (variant === 'gradient') {
    Component = GradientLogo;
  } else if (variant === 'solid') {
    Component = SolidLogo;
  } else if (variant === 'animated') {
    Component = AnimatedLogo;
  } else {
    throw new Error(`Unrecognized Logo variant: ${variant}`);
  }
  return /*#__PURE__*/React__default.createElement(Component, {
    variant: variant,
    size: size,
    stopColor1: stopColor1,
    stopColor2: stopColor2,
    color: color,
    sx: sxProp
  });
};
Logo.displayName = "Logo";
const GradientLogo = ({
  size,
  stopColor1,
  stopColor2
}) => /*#__PURE__*/React__default.createElement(SVG, {
  width: size,
  height: size,
  viewBox: "0 0 450 450",
  fill: "none",
  xmlns: "http://www.w3.org/2000/svg",
  "data-testid": "logo-var-grdnt"
}, /*#__PURE__*/React__default.createElement("path", {
  d: "M447.299 415.834L367.253 256.244C365.904 267.033 363.655 277.822 359.608 287.712C356.01 297.602 351.064 307.043 345.218 316.034L377.596 380.769L193.669 303.896C175.681 296.254 162.191 282.318 154.995 263.886C148.25 245.455 149.149 225.674 158.143 208.591L224.699 75.0749L291.254 208.142C299.798 225.674 301.147 245.455 294.402 263.437C287.656 281.868 274.165 295.804 255.728 303.447L254.828 303.896L306.544 325.475C320.484 312.887 331.277 297.153 338.022 279.62C349.265 249.5 347.466 215.784 333.076 187.013L245.385 13.037C241.787 4.94506 233.693 0 224.699 0C215.705 0 207.61 4.94506 204.012 13.037L116.771 187.463C102.381 216.234 100.582 249.95 111.824 280.07C123.067 310.19 146.451 334.466 176.131 347.053L417.619 448.202C420.317 449.55 423.465 450 426.613 450C432.909 450 439.205 447.303 443.701 442.807C449.997 435.165 451.346 424.376 447.299 415.834Z",
  fill: "url(#gradient0)"
}), /*#__PURE__*/React__default.createElement("path", {
  d: "M139.656 352.448L72.2012 380.769L104.579 316.484C98.7334 307.493 93.7867 298.052 90.1892 288.162C86.5916 277.822 83.8934 267.033 82.5443 256.693L2.49804 415.834C-1.99894 424.376 -0.200151 435.165 6.09562 441.908C10.5926 446.853 16.8884 449.101 23.1841 449.101C26.332 449.101 29.4799 448.651 32.1781 447.303L194.519 379.421L166.638 367.732C157.194 364.136 148.2 358.741 139.656 352.448Z",
  fill: "url(#gradient1)"
}), /*#__PURE__*/React__default.createElement("defs", null, /*#__PURE__*/React__default.createElement("linearGradient", {
  id: "gradient0",
  x1: "104.519",
  y1: "485.627",
  x2: "490.986",
  y2: "450.091",
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React__default.createElement(StopOne, {
  style: {
    '--stopColor1': stopColor1
  }
}), /*#__PURE__*/React__default.createElement(StopTwo, {
  offset: "1",
  style: {
    '--stopColor2': stopColor2
  }
})), /*#__PURE__*/React__default.createElement("linearGradient", {
  id: "gradient1",
  x1: "3.70362e-08",
  y1: "464.334",
  x2: "216.508",
  y2: "438.088",
  gradientUnits: "userSpaceOnUse"
}, /*#__PURE__*/React__default.createElement(StopOne, {
  style: {
    '--stopColor1': stopColor1
  }
}), /*#__PURE__*/React__default.createElement(StopTwo, {
  offset: "1",
  style: {
    '--stopColor2': stopColor2
  }
}))));
GradientLogo.displayName = "GradientLogo";
const SolidLogo = ({
  size,
  color
}) => {
  return /*#__PURE__*/React__default.createElement(SVG, {
    width: size,
    height: size,
    viewBox: "0 0 450 450",
    fill: "none",
    "data-testid": "logo-var-sld"
  }, /*#__PURE__*/React__default.createElement("path", {
    d: "M447.299 415.834L367.253 256.244C365.904 267.033 363.655 277.822 359.608 287.712C356.01 297.602 351.064 307.043 345.218 316.034L377.596 380.769L193.669 303.896C175.681 296.254 162.191 282.318 154.995 263.886C148.25 245.455 149.149 225.674 158.143 208.591L224.699 75.0749L291.254 208.142C299.798 225.674 301.147 245.455 294.402 263.437C287.656 281.868 274.165 295.804 255.728 303.447L254.828 303.896L306.544 325.475C320.484 312.887 331.277 297.153 338.022 279.62C349.265 249.5 347.466 215.784 333.076 187.013L245.385 13.037C241.787 4.94506 233.693 0 224.699 0C215.705 0 207.61 4.94506 204.012 13.037L116.771 187.463C102.381 216.234 100.582 249.95 111.824 280.07C123.067 310.19 146.451 334.466 176.131 347.053L417.619 448.202C420.317 449.55 423.465 450 426.613 450C432.909 450 439.205 447.303 443.701 442.807C449.997 435.165 451.346 424.376 447.299 415.834Z",
    fill: color
  }), /*#__PURE__*/React__default.createElement("path", {
    d: "M139.656 352.448L72.2012 380.769L104.579 316.484C98.7334 307.493 93.7867 298.052 90.1892 288.162C86.5916 277.822 83.8934 267.033 82.5443 256.693L2.49804 415.834C-1.99894 424.376 -0.200151 435.165 6.09562 441.908C10.5926 446.853 16.8884 449.101 23.1841 449.101C26.332 449.101 29.4799 448.651 32.1781 447.303L194.519 379.421L166.638 367.732C157.194 364.136 148.2 358.741 139.656 352.448Z",
    fill: color
  }));
};
SolidLogo.displayName = "SolidLogo";
var Logo$1 = Logo;

export { Logo$1 as default };
